/**
 * DECKSTR.GG — Arena Pipeline (Browser Edition)
 * ──────────────────────────────────────────────
 * Orchestrates the full MTGA tracking pipeline in the browser:
 *   FileWatcher → Router → Parser → Publisher → OBS Overlay
 *
 * This is the browser equivalent of bot/src/arena/index.js.
 */

import { Router, type RouterEvent } from './router';
import { ArenaParser, type ParserSnapshot } from './parser';
import { ArenaFileWatcher, type FileWatcherStatus } from './file-watcher';
import { BrowserPublisher, type GameStatePayload, type CardFocusPayload } from './browser-publisher';

// ─── Types ────────────────────────────────────────

export type PipelineStatus = 'idle' | 'selecting' | 'connected' | 'running' | 'error';

export interface PipelineState {
    status: PipelineStatus;
    matchActive: boolean;
    matchId: string | null;
    turnNumber: number | null;
    isMyTurn: boolean;
    myLife: number | null;
    oppLife: number | null;
    zones: {
        hand: number;
        library: number;
        graveyard: number;
        exile: number;
        battlefield: number;
        lands: number;
    };
    oppZones: {
        handSize: number;
        creatures: number;
        lands: number;
        openMana: number;
    };
    lastCardFocus: { name: string; transition: string } | null;
    snapshotCount: number;
    fileName: string;
    channelName: string;
    error: string | null;
}

export interface PipelineCallbacks {
    onState: (state: PipelineState) => void;
    onLog: (msg: string) => void;
    onCardFocus?: (card: CardFocusPayload) => void;
}

// ─── Scryfall Lookup (lightweight browser version) ──

interface ScryfallCard {
    name: string;
    imageUrl: string;
    grpId: number;
    typeLine?: string;
    cmc?: number;
}

const scryfallCache = new Map<number, ScryfallCard>();

async function resolveGrpId(grpId: number): Promise<ScryfallCard | null> {
    if (scryfallCache.has(grpId)) return scryfallCache.get(grpId)!;

    try {
        const resp = await fetch(`https://api.scryfall.com/cards/arena/${grpId}`);
        if (!resp.ok) return null;
        const card = await resp.json();

        const info: ScryfallCard = {
            name: card.name,
            imageUrl: card.image_uris?.normal || card.card_faces?.[0]?.image_uris?.normal || '',
            grpId,
            typeLine: card.type_line,
            cmc: card.cmc,
        };

        scryfallCache.set(grpId, info);
        return info;
    } catch {
        return null;
    }
}

// ─── Helper: detect seat ID from first game state ──

function detectMySeatId(event: RouterEvent): number | null {
    const messages = event.payload?.greToClientEvent?.greToClientMessages;
    if (!Array.isArray(messages)) return null;

    for (const msg of messages) {
        const gsm = msg?.gameStateMessage || msg?.connectResp?.gameStateMessage;
        if (!gsm) continue;

        // Method 1: systemSeatIds in ConnectResp
        if (msg?.connectResp?.systemSeatIds?.[0] != null) {
            return msg.connectResp.systemSeatIds[0];
        }

        // Method 2: Look for the player with the deck/library
        if (Array.isArray(gsm.zones) && Array.isArray(gsm.players)) {
            for (const zone of gsm.zones) {
                if (zone?.type === 'ZoneType_Library' && zone.ownerSeatId != null) {
                    // The first library zone usually belongs to us
                    const instanceCount = zone.objectInstanceIds?.length ?? 0;
                    if (instanceCount > 30) {
                        return zone.ownerSeatId;
                    }
                }
            }
        }
    }

    return null;
}

// ─── Pipeline Class ───────────────────────────────

export class ArenaPipeline {
    private _watcher: ArenaFileWatcher;
    private _router: Router;
    private _parser: ArenaParser;
    private _publisher: BrowserPublisher;
    private _callbacks: PipelineCallbacks;

    // Pipeline state
    private _matchActive: boolean = false;
    private _lastMatchId: string | null = null;
    private _seatDetected: boolean = false;
    private _snapshotCount: number = 0;
    private _lastCardFocus: { name: string; transition: string } | null = null;
    private _status: PipelineStatus = 'idle';
    private _error: string | null = null;

    constructor(channelName: string, callbacks: PipelineCallbacks) {
        this._callbacks = callbacks;

        this._watcher = new ArenaFileWatcher({
            pollMs: 250,
            onStatus: (status, msg) => {
                callbacks.onLog(`[watcher] ${status}${msg ? ': ' + msg : ''}`);
                if (status === 'connected') {
                    this._status = 'connected';
                } else if (status === 'error') {
                    this._status = 'error';
                    this._error = msg || 'File watcher error';
                }
                this._emitState();
            },
        });

        this._router = new Router();
        this._parser = new ArenaParser();

        this._publisher = new BrowserPublisher({
            channelName,
            throttleMs: 250,
            onLog: (msg) => callbacks.onLog(`[publisher] ${msg}`),
        });
    }

    get status(): PipelineStatus {
        return this._status;
    }

    /**
     * Step 1: Let the user select their Player.log file.
     */
    async selectFile(): Promise<boolean> {
        this._status = 'selecting';
        this._emitState();

        const ok = await this._watcher.selectLogFile();
        if (!ok) {
            this._status = 'idle';
            this._emitState();
            return false;
        }

        return true;
    }

    /**
     * Step 2: Start the pipeline (connect publisher + start file polling).
     */
    async start(): Promise<void> {
        // Connect publisher
        try {
            await this._publisher.connect();
            this._callbacks.onLog(`Publishing to: arena-gamestate-${this._publisher.channelName}`);
        } catch (err: any) {
            this._callbacks.onLog(`Publisher connect failed: ${err.message}`);
            // Continue without publishing — still useful for local display
        }

        // Start file watcher — each chunk goes through the pipeline
        this._watcher.start((chunk) => this._processChunk(chunk));

        this._status = 'running';
        this._callbacks.onLog('Pipeline running. Waiting for game events...');
        this._emitState();
    }

    /**
     * Stop the pipeline.
     */
    stop(): void {
        this._watcher.stop();
        this._publisher.disconnect();
        this._status = 'idle';
        this._matchActive = false;
        this._lastMatchId = null;
        this._seatDetected = false;
        this._snapshotCount = 0;
        this._lastCardFocus = null;
        this._error = null;
        this._router = new Router();
        this._parser = new ArenaParser();
        this._callbacks.onLog('Pipeline stopped.');
        this._emitState();
    }

    /**
     * Disconnect completely (stop + release file handle).
     */
    disconnect(): void {
        this.stop();
        this._watcher.disconnect();
    }

    // ─── Internal Pipeline Processing ─────────────

    private _processChunk(chunkText: string): void {
        const events = this._router.pushChunk(chunkText);

        for (const event of events) {
            this._processEvent(event);
        }
    }

    private _processEvent(event: RouterEvent): void {
        if (event.kind === 'MATCH_STATE_CHANGE') {
            this._handleMatchStateChange(event);
            return;
        }

        if (event.kind !== 'GRE_TO_CLIENT') return;

        // Auto-detect seat ID
        if (!this._seatDetected) {
            const seatId = detectMySeatId(event);
            if (seatId != null) {
                this._parser.setMySeatId(seatId);
                this._seatDetected = true;
                this._callbacks.onLog(`Seat detected: ${seatId}`);
            }
        }

        // Auto-detect match start
        const matchId = event.matchId;
        if (matchId && matchId !== this._lastMatchId) {
            this._lastMatchId = matchId;
            this._matchActive = true;
            this._seatDetected = false;
            this._snapshotCount = 0;

            this._parser.reset();
            this._callbacks.onLog(`▶ Match started: ${matchId}`);
            this._publisher.publishMatchEvent('match_start', { matchId });
        }

        // Feed through parser
        this._parser.processEvent(event);

        // Get snapshot
        const snapshot = this._parser.getSnapshot();
        this._snapshotCount++;

        // Publish game state (every 4th snapshot ≈ 1 second)
        if (this._snapshotCount % 4 === 0) {
            this._publishGameState(snapshot);
        }

        // Process card focus events
        this._processCardFocus(snapshot);

        // Emit state to UI
        if (this._snapshotCount % 2 === 0) {
            this._emitState();
        }
    }

    private _handleMatchStateChange(event: RouterEvent): void {
        const payload = event.payload;
        const stateChange = payload?.matchGameRoomStateChangedEvent;

        if (stateChange?.gameRoomInfo?.stateType === 'MatchGameRoomStateType_MatchCompleted') {
            this._matchActive = false;
            this._callbacks.onLog('■ Match completed');
            this._publisher.publishMatchEvent('match_end', {
                matchId: this._lastMatchId,
            });
            this._emitState();
        }
    }

    private _publishGameState(snap: ParserSnapshot): void {
        const payload: GameStatePayload = {
            turn: snap.turnInfo?.turnNumber ?? null,
            activePlayer: snap.turnInfo?.activePlayer ?? null,
            phase: snap.turnInfo?.phase ?? null,
            myLife: snap.myLifeTotal,
            oppLife: snap.opponentLifeTotal,
            zones: {
                hand: snap.myCardsInHand.length,
                library: 0, // Not tracked without tracker layer
                graveyard: snap.myCardsInGraveyard.length,
                exile: snap.myCardsInExile.playable.length + snap.myCardsInExile.unplayable.length,
                battlefield: snap.myCardsInPlay.length,
                lands: snap.myLandsInPlay.length,
            },
            oppZones: {
                handSize: snap.oppBoard.handSize,
                creatures: snap.oppBoard.totalCreatureCount,
                lands: snap.oppBoard.lands.length,
                openMana: snap.oppBoard.openMana,
            },
        };

        this._publisher.publishGameState(payload);
    }

    private async _processCardFocus(snap: ParserSnapshot): Promise<void> {
        const transfers = snap.zoneTransfersThisDiff || [];
        if (transfers.length === 0) return;

        // Prioritize interesting transitions
        const priorityOrder = ['ZoneType_Stack', 'ZoneType_Battlefield', 'ZoneType_Hand', 'ZoneType_Graveyard', 'ZoneType_Exile'];

        let bestTransfer: any = null;
        let bestPriority = Infinity;

        for (const t of transfers) {
            const dstType = t.dstZoneType || '';
            const srcType = t.srcZoneType || '';

            // Skip library-to-library shuffles
            if (srcType.includes('Library') && dstType.includes('Library')) continue;

            const idx = priorityOrder.findIndex(p => dstType.includes(p.replace('ZoneType_', '')));
            if (idx !== -1 && idx < bestPriority) {
                bestPriority = idx;
                bestTransfer = t;
            }
        }

        if (!bestTransfer) return;

        // Find grpId for this card
        const iid = bestTransfer.instanceId;
        const allCards = [
            ...snap.myCardsInHand,
            ...snap.myCardsOnStack,
            ...snap.myCardsInPlay,
            ...snap.myLandsInPlay,
            ...snap.myCardsInGraveyard,
            ...snap.myCardsInExile.playable,
            ...snap.myCardsInExile.unplayable,
        ];

        const card = allCards.find(c => c.instanceId === iid);
        const grpId = card?.grpId;

        if (!grpId) return;

        // Resolve via Scryfall
        const cardInfo = await resolveGrpId(grpId);
        if (!cardInfo || !cardInfo.imageUrl) return;

        // Determine transition type
        const dstType = bestTransfer.dstZoneType || '';
        const srcType = bestTransfer.srcZoneType || '';
        let transition = 'play';
        if (dstType.includes('Hand')) transition = 'draw';
        else if (dstType.includes('Stack')) transition = 'cast';
        else if (dstType.includes('Graveyard')) transition = 'die';
        else if (dstType.includes('Exile')) transition = 'exile';
        else if (dstType.includes('Battlefield') && srcType.includes('Stack')) transition = 'resolve';

        const payload: CardFocusPayload = {
            name: cardInfo.name,
            imageUrl: cardInfo.imageUrl,
            grpId: cardInfo.grpId,
            transition,
            type: cardInfo.typeLine,
            cmc: cardInfo.cmc,
        };

        this._publisher.publishCardFocus(payload);
        this._lastCardFocus = { name: cardInfo.name, transition };
        this._callbacks.onCardFocus?.(payload);
        this._callbacks.onLog(`[card] ${transition}: ${cardInfo.name}`);
    }

    // ─── State Emission ───────────────────────────

    private _emitState(): void {
        const snap = this._parser.getSnapshot();

        const state: PipelineState = {
            status: this._status,
            matchActive: this._matchActive,
            matchId: this._lastMatchId,
            turnNumber: snap.turnInfo?.turnNumber ?? null,
            isMyTurn: snap.turnInfo?.activePlayer === snap.mySeatId,
            myLife: snap.myLifeTotal,
            oppLife: snap.opponentLifeTotal,
            zones: {
                hand: snap.myCardsInHand.length,
                library: 0,
                graveyard: snap.myCardsInGraveyard.length,
                exile: snap.myCardsInExile.playable.length + snap.myCardsInExile.unplayable.length,
                battlefield: snap.myCardsInPlay.length,
                lands: snap.myLandsInPlay.length,
            },
            oppZones: {
                handSize: snap.oppBoard.handSize,
                creatures: snap.oppBoard.totalCreatureCount,
                lands: snap.oppBoard.lands.length,
                openMana: snap.oppBoard.openMana,
            },
            lastCardFocus: this._lastCardFocus,
            snapshotCount: this._snapshotCount,
            fileName: this._watcher.fileName,
            channelName: this._publisher.channelName,
            error: this._error,
        };

        this._callbacks.onState(state);
    }
}
